/**
 * components/avatar.tsx
 * The one common Avatar for the whole app. Premium by default — soft tonal
 * gradient fill (web), crisp tinted ring, balanced initials, optional photo.
 *
 *  • shape   'circle'  → people (recruiters, members)
 *            'rounded' → roles / entities / openings (rounded-square tile)
 *  • tone    semantic colour family that tints fill + ring + text
 *            'default' keeps the calm neutral look (back-compat)
 *  • color   explicit solid background (white text) — overrides tone (back-compat)
 *  • size    named step ('xs'…'xl') or any pixel number
 *  • ring    adds a soft outer halo ring for an elevated, "premium" feel
 *
 * Initials are derived from `name` (up to `maxInitials` words). Roles usually
 * pass maxInitials={1}; people default to 2.
 */
import React from 'react';
import {
  View,
  Text,
  Image,
  Platform,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { useTheme } from '../context/themeContext';

export type AvatarSize = 'xs' | 'sm' | 'default' | 'lg' | 'xl';
export type AvatarShape = 'circle' | 'rounded';
export type AvatarTone =
  | 'default' | 'accent' | 'info' | 'success' | 'warn' | 'danger' | 'neutral';

interface AvatarProps {
  name?: string;
  src?: string;
  size?: AvatarSize | number;
  shape?: AvatarShape;
  tone?: AvatarTone;
  color?: string; // explicit background color; sets text to white (back-compat)
  /** Vivid two-tone gradient fill + white initials + soft glow (premium). */
  gradientColors?: [string, string];
  ring?: boolean;
  maxInitials?: number;
  style?: ViewStyle;
}

const SIZE_MAP: Record<AvatarSize, number> = {
  xs: 18, sm: 24, default: 32, lg: 44, xl: 56,
};

export default function Avatar({
  name = '?',
  src,
  size = 'default',
  shape = 'circle',
  tone = 'default',
  color,
  gradientColors,
  ring = false,
  maxInitials = 2,
  style,
}: AvatarProps) {
  const { theme } = useTheme();
  const { colors, fontWeight } = theme;
  const isWeb = Platform.OS === 'web';

  const dim = typeof size === 'number' ? size : SIZE_MAP[size];
  const fSize = Math.round(dim * (shape === 'rounded' ? 0.38 : 0.36));
  const radius = shape === 'rounded' ? Math.round(dim * 0.28) : dim / 2;

  // Initials: first letter of each of the first `maxInitials` words.
  const initials =
    name.trim().split(/\s+/).slice(0, Math.max(1, maxInitials))
      .map((w) => w[0] ?? '').join('').toUpperCase() || '?';

  // Resolve the colour set. Explicit `color` wins (solid + white text).
  // A semantic `tone` tints fill/ring/text; 'default' is the calm neutral look.
  let bg: string;
  let fg: string;
  let line: string;
  if (gradientColors) {
    bg = gradientColors[0]; fg = colors.white; line = colors.transparent;
  } else if (color) {
    bg = color; fg = colors.white; line = colors.transparent;
  } else if (tone === 'default') {
    bg = colors.paper3; fg = colors.ink2; line = colors.line;
  } else {
    bg = colors[`${tone}Soft` as keyof typeof colors] as string;
    fg = colors[tone] as string;
    line = colors[`${tone}Line` as keyof typeof colors] as string;
  }

  const containerStyle: ViewStyle = {
    width: dim,
    height: dim,
    borderRadius: radius,
    backgroundColor: bg,
    borderWidth: 1,
    borderColor: line,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  };

  // Subtle tonal gradient on web for the premium sheen (no-op on native;
  // solid `bg` already set as the fallback).
  const gradient: any = isWeb && !src
    ? gradientColors
      ? { backgroundImage: `linear-gradient(145deg, ${gradientColors[0]} 0%, ${gradientColors[1]} 100%)`, boxShadow: `0 4px 12px -3px ${gradientColors[1]}88, inset 0 1px 0 rgba(255,255,255,0.25)` }
      : { backgroundImage: `linear-gradient(150deg, ${bg} 0%, ${colors.card} 118%)` }
    : null;

  const labelStyle: TextStyle = {
    fontSize: fSize,
    fontWeight: fontWeight.bold,
    color: fg,
    fontFamily: theme.font.sans,
    textTransform: 'uppercase',
    includeFontPadding: false,
    lineHeight: fSize * 1.2,
    letterSpacing: 0.2,
  };

  const inner = src ? (
    <View style={[containerStyle, style]}>
      <Image
        source={{ uri: src }}
        style={{ width: dim, height: dim, borderRadius: radius }}
        resizeMode="cover"
        accessibilityLabel={name}
      />
    </View>
  ) : (
    <View style={[containerStyle, gradient, style]}>
      <Text style={labelStyle} numberOfLines={1}>{initials}</Text>
    </View>
  );

  if (!ring) return inner;

  // Premium halo ring — a soft padded wrapper that echoes the tone.
  const ringPad = Math.max(2, Math.round(dim * 0.06));
  return (
    <View
      style={{
        padding: ringPad,
        borderRadius: shape === 'rounded' ? radius + ringPad : 999,
        backgroundColor: color || tone === 'default' ? colors.paper2 : (colors[`${tone}Soft` as keyof typeof colors] as string),
        borderWidth: 1,
        borderColor: color || tone === 'default' ? colors.line : (colors[`${tone}Line` as keyof typeof colors] as string),
      }}
    >
      {inner}
    </View>
  );
}
